import React from 'react';
import {View, SafeAreaView, ScrollView} from 'react-native';
import makeStyles from './styles.web';
import Button from '../../components/elements/Button';
import Text from '../../components/elements/Text';
import Checkbox from '../../components/elements/Checkbox';
import Divider from '../../components/elements/Divider';
import BreadCrumbs from '../../components/elements/BreadCrumbs';
import MobilePhoneInput from '../../components/elements/MobilePhoneInput';
import Input from '../../components/elements/Input';
import {BUTTON_TYPES} from '../../utils/Constants';
import {useTheme, useThemedStyles} from '../../theme';
import {tID} from '../../utils/TestIdHelper';
import {getCountryCode} from '../../utils/HelperMethods';
import {ErrorBottomExpandableDrawer} from '.';
import CommonStyles from '../../utils/CommonStyles';
import useMediaQuery from '../../hooks/use-media-query';
import {
  ClosedEyeIcon,
  OpenEyeIcon,
  LogoGroup,
  LeftArrowIcon,
} from '../../utils/SvgIcons';

const UI = ({
  navigation,
  bottomModalVisible,
  onPressRequestFailed,
  firstName,
  setFirstName,
  validFirstName,
  firstNameError,
  lastName,
  setLastName,
  validLastName,
  lastNameError,
  email,
  setEmail,
  validEmail,
  emailError,
  password,
  setPassword,
  validedPassword,
  passwordError,
  showPassword,
  onPressShowPassword,
  repeatPassword,
  setRepeatPassword,
  repeatPasswordError,
  validRepeatPassword,
  showRepeatPassword,
  onPressShowRepeatPassword,
  phoneNumber,
  onChangePhoneNumber,
  validPhoneNumber,
  phoneNumberError,
  navigateToTermsOfService,
  navigateToPrivacyPolicy,
  checkboxSelected,
  onPressCheckbox,
  isValidForm,
  saveDetails,
  moveToNextStep = () => {},
}) => {
  const isDesktop = useMediaQuery('(min-width: 1200px)');
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);

  return (
    <SafeAreaView
      testID={tID('AccountDetails-SafeAreaView-Main')}
      style={styles.mainView}>
      <ErrorBottomExpandableDrawer
        visible={bottomModalVisible}
        onClose={onPressRequestFailed}
      />
      <ScrollView
        testID={tID('AccountDetails-ScrollView-1')}
        style={styles.formView}>
        <LogoGroup width={146} height={40} style={CommonStyles.mv_40} />
        <View
          testID={tID('AccountDetails-View-SigninRouteContainer')}
          style={styles.signinRouteContainer}>
          <LeftArrowIcon
            width={5}
            height={9}
            tintColor={colors.link}
          />
          <Text
            testID={tID('AccountDetails-Text-SigninRoute')}
            variation="Text1SemiBold"
            color={colors.link}
            lineHeight={21}
            style={CommonStyles.ml_12}>
            Sign-In
          </Text>
        </View>
        <Text
          testID={tID('AccountDetails-Text-Title')}
          variation="HeadlineBold28"
          color={colors.textPrimary}
          style={CommonStyles.mb_12}>
          Provide Your Details
        </Text>
        <Text
          testID={tID('AccountDetails-Text-SubTitle')}
          variation="Body1Regular"
          color={colors.textSecondary}
          style={CommonStyles.mb_8}>
          Add your personal information to create your account
        </Text>
        <View
          testID={tID('AccountDetails-View-BreadCrumbsContainer')}
          style={CommonStyles.mb_22}>
          <BreadCrumbs
            current={1}
            total={3}
            activeColor={colors.link}
            currentPassiveColor={colors.link}
            passiveColor={colors.borderMuted}
            activeDotWidth={24}
            activeDotHeight={12}
            currentPassiveDotWidth={12}
            currentPassiveDotHeight={12}
            passiveDotWidth={12}
            passiveDotHeight={12}
            containerStyle={styles.marginLeftMinus4}
            rightDotStyle={styles.marginTopMinus4}
          />
        </View>
        <Divider />
        <View
          testID={tID('AccountDetails-View-FormContainer')}
          style={CommonStyles.mv_32}>
          <View style={isDesktop && CommonStyles.flex_row}>
            <Input
              testID={tID('AccountDetails-Input-FirstName')}
              inputTitle="First Name"
              placeholder="Type Here..."
              errorText="Please enter first name."
              value={firstName}
              onChangeText={setFirstName}
              onBlur={validFirstName}
              hasError={firstNameError}
              containerStyle={styles.leftInput(isDesktop)}
            />
            <Input
              testID={tID('AccountDetails-Input-LastName')}
              inputTitle="Last Name"
              placeholder="Type Here..."
              errorText="Please enter last name."
              value={lastName}
              onChangeText={setLastName}
              onBlur={validLastName}
              hasError={lastNameError}
              containerStyle={styles.rightInput(isDesktop)}
            />
          </View>
          <View
            style={[
              isDesktop && CommonStyles.flex_row,
              isDesktop ? CommonStyles.mt_24 : CommonStyles.mt_16,
            ]}>
            <Input
              testID={tID('AccountDetails-Input-Email')}
              inputTitle="Email Address"
              placeholder="Type Here..."
              errorText="Please enter a valid Email Address."
              value={email}
              onChangeText={setEmail}
              onBlur={validEmail}
              hasError={emailError}
              keyboardType={'email-address'}
              containerStyle={styles.leftInput(isDesktop)}
            />
            <MobilePhoneInput
              phoneNumber={phoneNumber}
              onChangePhoneNumber={onChangePhoneNumber}
              onBlur={validPhoneNumber}
              leftText={getCountryCode()}
              hasError={phoneNumberError}
              placeholder={''}
              showFlag={false}
              containerStyle={styles.rightInput(isDesktop)}
            />
          </View>
          <View style={CommonStyles.mt_24}>
            <Text
              testID={tID('AccountDetails-Text-PasswordTitle')}
              variation="Body1Bold18"
              color={colors.textPrimary}
              style={CommonStyles.mb_12}>
              Create Password
            </Text>
            <Text
              testID={tID('AccountDetails-Text-PasswordSubTitle')}
              variation="Text1Regular"
              color={colors.textMuted}
              style={CommonStyles.mb_8}>
              Password must be at least 8 characters and contain at least one
              capital letter, one special character, one number, and cannot be a
              common password.
            </Text>
            <View style={isDesktop && CommonStyles.flex_row}>
              <Input
                testID={tID('AccountDetails-Input-Password')}
                inputTitle="Password"
                placeholder={'Type here...'}
                value={password}
                onChangeText={setPassword}
                onBlur={validedPassword}
                errorText="Password doesn’t meet the requirements."
                secureTextEntry={!showPassword}
                hasError={passwordError}
                rightIcon={showPassword ? <OpenEyeIcon /> : <ClosedEyeIcon />}
                onPressRightIcon={onPressShowPassword}
                containerStyle={styles.leftInput(isDesktop)}
              />
              <Input
                testID={tID('AccountDetails-Input-RepeatPassword')}
                inputTitle="Repeat Password"
                placeholder="Type Here..."
                value={repeatPassword}
                onChangeText={setRepeatPassword}
                onBlur={validRepeatPassword}
                errorText="Passwords do not match."
                secureTextEntry={!showRepeatPassword}
                hasError={repeatPasswordError}
                rightIcon={
                  showRepeatPassword ? <OpenEyeIcon /> : <ClosedEyeIcon />
                }
                onPressRightIcon={onPressShowRepeatPassword}
                containerStyle={styles.rightInput(isDesktop)}
              />
            </View>
          </View>
          <View style={CommonStyles.mt_32}>
            <Checkbox
              testID={tID('AccountDetails-Checkbox-AcceptPrivacyPolicy')}
              topMargin={0}
              checkboxStyle={styles.checkbox}
              title={
                <>
                  <Text
                    testID={tID('AccountDetails-Text-CheckboxLabel1')}
                    lineHeight={21}
                    variation="Text1Regular"
                    color={colors.textPrimary}>
                    I accept the{' '}
                    <Text
                      testID={tID('AccountDetails-Text-CheckboxLabel2')}
                      variation="Text1SemiBold"
                      color={colors.textPrimary}
                      style={CommonStyles.underlined_text}
                      onPress={navigateToTermsOfService}>
                      {'Diathrive Health Terms of Service'}
                    </Text>{' '}
                    and have read the{' '}
                    <Text
                      testID={tID('AccountDetails-Text-CheckboxLabel3')}
                      variation="Text1SemiBold"
                      color={colors.textPrimary}
                      style={CommonStyles.underlined_text}
                      onPress={navigateToPrivacyPolicy}>
                      {'Privacy Policy.'}
                    </Text>
                  </Text>
                </>
              }
              selected={checkboxSelected}
              onPress={onPressCheckbox}
            />
          </View>
        </View>
      </ScrollView>
      <View style={styles.buttonContainer}>
        <Button
          testID={tID('AccountDetails-Button-Cancel')}
          title={'Cancel'}
          titleColor={colors.link}
          onPress={() => moveToNextStep(1)}
          style={styles.cancelButton}
        />
        <Button
          testID={tID('AccountDetails-Button-Next')}
          onPress={() => saveDetails()}
          title={'Next'}
          buttonType={BUTTON_TYPES.Filled}
          titleColor={colors.textOnAccent}
          disabled={!isValidForm}
          style={styles.nextButton}
        />
      </View>
    </SafeAreaView>
  );
};

export default UI;
